import { createFileRoute } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { listClients, type ClientsSearchParams } from "../components/clients/api";
import { ClientsSectionTabs } from "../components/clients/ClientsSectionTabs";
import { ClientsTabs } from "../components/clients/ClientsTabs";
import { ClientsTable } from "../components/clients/ClientsTable";
import { ClientsFilters } from "../components/clients/ClientsFilters";
import { NewTestMemberButton } from "../components/relations/NewTestMemberButton";

const searchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(["prospect", "member", "lapsed", "flagged"]).optional(),
  tag: z.array(z.string()).optional(),
  tier: z.array(z.string()).optional(),
  cycle: z.array(z.enum(["new", "renewal", "churn_return", "churn"])).optional(),
  membership: z.array(z.enum(["active", "renewing"])).optional(),
  language: z.array(z.enum(["ka", "en"])).optional(),
  page: z.number().int().min(1).default(1),
  page_size: z.number().int().min(1).max(200).default(50),
  sort: z
    .enum([
      "name",
      "-name",
      "last_visit",
      "-last_visit",
      "lifetime",
      "-lifetime",
      "loyalty",
      "-loyalty",
      "visits",
      "-visits",
      "renewals",
      "-renewals",
      "last_comm",
      "-last_comm",
    ])
    .default("-last_visit"),
  last_visit_within: z.enum(["7", "30", "90", "lapsed"]).optional(),
  last_comm_within: z.enum(["7", "30", "90", "over90", "never"]).optional(),
  spend_min: z.number().min(0).optional(),
  spend_max: z.number().min(0).optional(),
  min_renewals: z.number().int().min(1).optional(),
  hide_hotel_guests: z.boolean().optional(),
  favorites: z.boolean().optional(),
});

export const Route = createFileRoute("/_auth/clients/")({
  staticData: { titleKey: "nav.clients", breadcrumbKey: "breadcrumb.operate", breadcrumbN: 3 },
  validateSearch: searchSchema,
  component: ClientsDirectory,
});

function ClientsDirectory() {
  const search = Route.useSearch() as ClientsSearchParams;

  const { data, isLoading, error } = useQuery({
    queryKey: ["clients", search],
    queryFn: () => listClients(search),
    // Keep showing the current rows while the next page/filter loads — the
    // table dims instead of flashing skeletons on every navigation.
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <ClientsSectionTabs active="directory" />
      <ClientsTabs
        active={search.status}
        favoritesActive={search.favorites === true}
        action={<NewTestMemberButton />}
      />
      <ClientsFilters />
      <ClientsTable
        items={data?.items}
        isLoading={isLoading}
        error={error}
        page={data?.page}
        pageSize={data?.page_size}
        total={data?.total}
      />
    </div>
  );
}
